import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useApiQuery, type Api } from "../../services/api";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { getRoomLogoUrl } from "../../domains/room/utils/getRoomLogoUrl";
import { getRoomName } from "../../domains/room/hooks/useRoomName";
import { DesktopRoomLine } from "../room/DesktopRoomLine";
import { useNow } from "../../common/hooks/useNow";
import { deriveCongressStatus } from "../../domains/congress/congressStatus";
import { DesktopFollowRoomButton } from "../room/DesktopFollowRoomButton";
import Skeleton from "../skeleton/Skeleton";

/** How many rooms each section lists. */
const RAIL_ROOM_COUNT = 3;

type JoinedRoom =
  Api["get"]["/webapp/private/rooms"]["output"]["body"]["rooms"][number];

function RailSectionTitle({
  title,
  onSeeAll,
}: {
  title: string;
  onSeeAll: (() => void) | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex h-5 items-center justify-between gap-2">
      <h2 className="m-0 min-w-0 flex-1 truncate text-footnote font-semibold text-ds-slate-700">
        {title}
      </h2>
      {onSeeAll === null ? null : (
        <button
          type="button"
          onClick={onSeeAll}
          className="shrink-0 cursor-pointer text-footnote font-medium text-ds-mint-800 hover:underline"
        >
          {t("See all")}
        </button>
      )}
    </div>
  );
}

/** A rail section while the rooms load: its title, then its room lines. */
function RailSectionSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="rounded" width="40%" height={20} />
      {Array.from({ length: RAIL_ROOM_COUNT }).map((_, idx) => (
        <div key={idx} className="flex items-start gap-2.5">
          <Skeleton className="shrink-0 rounded-lg" width={32} height={32} />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <Skeleton className="rounded" width="80%" height={16} />
            <Skeleton className="rounded" width="50%" height={12} />
          </div>
        </div>
      ))}
    </div>
  );
}

const MINUTE_MS = 60_000;

/**
 * One room the reader follows. It reads LIVE beside its name while one of its
 * congress spaces is on (Figma "RoomLine · LIVE (pendant le congrès)"), from
 * the room's own spaces, promoted on Discovery or not.
 */
function YourRoomLine({ room }: { room: JoinedRoom }): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { activeLang, contentLang } = useActiveLang();
  const now = useNow(MINUTE_MS);
  const spacesQuery = useApiQuery("/webapp/private/rooms/:id/congress-spaces", {
    param: { id: room.id },
    query: { language: contentLang },
  });
  const isLive =
    spacesQuery.state === "success" &&
    spacesQuery.body.some(
      (space) => deriveCongressStatus(space, now).kind === "live"
    );
  return (
    <DesktopRoomLine
      name={getRoomName(room, activeLang)}
      nameBadge={
        isLive ? (
          <span className="shrink-0 rounded bg-ds-strawberry-600 px-1 text-caption font-medium text-ds-white uppercase">
            {t("Live")}
          </span>
        ) : null
      }
      logoUrl={getRoomLogoUrl(room)}
      subtitle={t("by {{name}}", {
        name: room.organisationModel.name,
      })}
      action={null}
      onClick={() => {
        history.push(`/room/discovery/${room.id}`);
      }}
    />
  );
}

/**
 * The rooms the reader follows, a few of them (Figma "DesktopRail / Your
 * rooms"): "See all" leads to the Rooms page, which lists them all.
 */
function YourRoomsSection({
  rooms,
}: {
  rooms: ReadonlyArray<JoinedRoom>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  return (
    <section className="flex flex-col gap-4">
      <RailSectionTitle
        title={t("Your rooms")}
        onSeeAll={() => {
          history.push("/rooms");
        }}
      />
      {rooms.slice(0, RAIL_ROOM_COUNT).map((room) => (
        <YourRoomLine key={room.id} room={room} />
      ))}
    </section>
  );
}

/**
 * The right rail of the desktop Explore (Figma "DesktopRail"): the rooms the
 * reader follows, then rooms to discover with a Follow button each. Each room
 * reads "by" its organisation, in both sections.
 *
 * It fills the column the page gives it: the page owns the rail's width and
 * the rule that parts it from the feed.
 */
function DesktopExploreRail(): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { activeLang } = useActiveLang();
  const joinedRoomsQuery = useApiQuery("/webapp/private/rooms", {});
  const recommendedRoomsQuery = useApiQuery(
    "/webapp/private/rooms/recommended",
    {}
  );
  const publicRoomsQuery = useApiQuery("/webapp/public/rooms", {});

  const allJoinedRooms =
    joinedRoomsQuery.state === "success" ? joinedRoomsQuery.body.rooms : [];
  // The Rooms page's discovery order: the recommended rooms first, then the
  // other public rooms, so the section is not empty for a reader the
  // recommender has nothing for. Follow revalidates the joined rooms only, so
  // a room just followed is still in both answers: it is dropped here.
  const joinedIds = new Set(allJoinedRooms.map((room) => room.id));
  const recommended =
    recommendedRoomsQuery.state === "success"
      ? recommendedRoomsQuery.body.rooms
      : [];
  const publicRooms =
    publicRoomsQuery.state === "success" ? publicRoomsQuery.body : [];
  const recommendedIds = new Set(recommended.map((room) => room.id));
  // Only once the joined rooms are known: before that, a room the reader
  // already follows would show here with a Follow button.
  const roomsToDiscover =
    joinedRoomsQuery.state !== "success"
      ? []
      : [
          ...recommended,
          ...publicRooms.filter((room) => !recommendedIds.has(room.id)),
        ]
          .filter((room) => !joinedIds.has(room.id))
          .slice(0, RAIL_ROOM_COUNT);

  // Both sections wait on the joined rooms (see `roomsToDiscover`), and rooms
  // to discover on its own two answers too.
  const discoverPending =
    recommendedRoomsQuery.state === "pending" ||
    publicRoomsQuery.state === "pending";
  if (joinedRoomsQuery.state === "pending")
    return (
      <aside className="flex w-full flex-col gap-10 pt-2">
        <RailSectionSkeleton />
        <RailSectionSkeleton />
      </aside>
    );

  return (
    <aside className="flex w-full flex-col gap-10 pt-2">
      {allJoinedRooms.length === 0 ? null : (
        <YourRoomsSection rooms={allJoinedRooms} />
      )}
      {discoverPending ? (
        <RailSectionSkeleton />
      ) : roomsToDiscover.length === 0 ? null : (
        <section className="flex flex-col gap-4">
          <RailSectionTitle
            title={t("Rooms to discover")}
            onSeeAll={() => {
              history.push("/rooms");
            }}
          />
          {roomsToDiscover.map((room) => (
            <DesktopRoomLine
              nameBadge={null}
              key={room.id}
              name={getRoomName(room, activeLang)}
              logoUrl={getRoomLogoUrl(room)}
              subtitle={t("by {{name}}", {
                name: room.organisationModel.name,
              })}
              action={<DesktopFollowRoomButton roomId={room.id} />}
              onClick={() => {
                history.push(`/room/discovery/${room.id}`);
              }}
            />
          ))}
        </section>
      )}
    </aside>
  );
}

export {
  DesktopExploreRail,
  RailSectionSkeleton,
  RailSectionTitle,
  YourRoomsSection,
};
